// src/pages/AdminPanel.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Importy komponentów
import EditUsersModal from "../components/EditUsersModal";
import ResetPasswordModal from "../components/ResetPasswordModal";
import AddClientModal from '../components/AddClientModal';
import AddAreaModal from '../components/AddAreaModal';
import DeleteAreaClientModal from "../components/DeleteAreaClientModal";
import SystemOrderGeneratorModal from "../components/SystemOrderGeneratorModal";
import WorkTimeStatisticsModal from "../components/WorkTimeStatisticsModal";
import CloseOrdersModal from "../components/CloseOrdersModal";
import AssignOrdersModal from "../components/AssignOrdersModal";
import InvoiceOrdersModal from "../components/InvoiceOrdersModal";

// Importuj dedykowany plik CSS
import "./AdminPanel.css";
import { API_URL } from "../config";

function AdminPanel() {
    const navigate = useNavigate();
    const currentUser = JSON.parse(localStorage.getItem("user"));

    /* ---------------- STANY DLA WSZYSTKICH MODALI ---------------- */
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [showSystemModal, setShowSystemModal] = useState(false);
    const [showAddClientModal, setShowAddClientModal] = useState(false);
    const [showAddAreaModal, setShowAddAreaModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [message, setMessage] = useState("");

    /* --- Stany dla formularza dodawania użytkownika --- */
    const [formData, setFormData] = useState({
        login: "", email: "", first_name: "", last_name: "", birth_date: "",
        hire_date: "", departure_date: "", role: "User", position: "Pracownik",
    });

    /* --- Stany dla funkcji z Dashboardu --- */
    const [ordersToClose, setOrdersToClose] = useState([]);
    const [loadingClose, setLoadingClose] = useState(false);
	
	const [ordersToInvoice, setOrdersToInvoice] = useState([]);
	const [loadingInvoice, setLoadingInvoice] = useState(false);
	const [showInvoiceModal, setShowInvoiceModal] = useState(false);
	
    const [selectedOption, setSelectedOption] = useState("");
    const [statsData, setStatsData] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [statsError, setStatsError] = useState("");
	
	const [showAssignOrdersModal, setShowAssignOrdersModal] = useState(false);

    // >>> DODANE: licznik zleceń wymagających przydzielenia <<<
    const [assignableCount, setAssignableCount] = useState(0);
    const [loadingAssignable, setLoadingAssignable] = useState(false);

    const isAdminOrBoss = currentUser && (currentUser.role === "Admin" || currentUser.position === "Szef");

    /* ---------------- HANDLERY ---------------- */
    
    // Handler dla formularza dodawania użytkownika
    const handleChange = ({ target: { name, value } }) =>
        setFormData((p) => ({ ...p, [name]: value }));

    const handleAddUser = async (e) => {
        e.preventDefault();
        setMessage(""); // Wyczyść poprzednie komunikaty

        try {
            const r = await fetch(
                `${API_URL}/addUser`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify(formData),
                }
            );

            if (!r.ok) {
                const errorData = await r.json().catch(() => ({ error: "Nieznany błąd serwera." }));
                throw new Error(errorData.error || "Wystąpił błąd.");
            }

            setMessage("✅ Użytkownik dodany, e-mail z hasłem został wysłany.");
            setShowAddModal(false); // Zamknij modal po sukcesie
            setFormData({
                login: "", email: "", first_name: "", last_name: "", birth_date: "",
                hire_date: "", departure_date: "", role: "User", position: "Pracownik",
            });
        } catch (err) {
            setMessage(`❌ Błąd: ${err.message}`);
        }
    };

    // Handler do zleceń do zamknięcia
    useEffect(() => {
        if (isAdminOrBoss) {
		  fetchOrdersToClose();
		  fetchOrdersToInvoice();
	    }
    }, [isAdminOrBoss]);
	
	const fetchOrdersToInvoice = async () => {
	  setLoadingInvoice(true);
	  try {
		const res = await fetch(`${API_URL}/orders/to-close`, { credentials: "include" });
		if (!res.ok) throw new Error("Błąd pobierania zleceń do fakturowania");
		const data = await res.json();
		setOrdersToInvoice(data.filter(o => o.status === "Fakturowanie"));
	  } catch (err) {
		console.error(err);
	  } finally {
		setLoadingInvoice(false);
	  }
	};

    const fetchOrdersToClose = async () => {
        setLoadingClose(true);
        try {
            const res = await fetch(
                `${API_URL}/orders/to-close`,
                { credentials: "include" }
            );
            if (!res.ok) throw new Error("Błąd pobierania zleceń do zamknięcia");
            setOrdersToClose(await res.json());
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingClose(false);
        }
    };

    const handleCloseOrder = async (orderId) => {
        try {
            const res = await fetch(`${API_URL}/orders/${orderId}/close`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ osoba_zamykajaca_id: currentUser.id }),
            });
            if (!res.ok) throw new Error("Błąd zamykania zlecenia");
            fetchOrdersToClose(); // Odśwież listę
			fetchOrdersToInvoice();
        } catch (err) {
            console.error(err);
        }
    };

    // Handlery dla statystyk
    const statsOptions = ["Mój Czas Pracy", "Czas Pracy Pracownika", "Czas Pracy Zlecenia"];

    const handleOptionClick = (opt) => {
        setSelectedOption(opt);
        if (opt === "Mój Czas Pracy") fetchMyTimeStats();
        else setStatsData(null);
        setShowStatsModal(true);
    };

    const fetchMyTimeStats = async () => {
        setLoadingStats(true);
        setStatsError("");
        try {
            const res = await fetch(`${API_URL}/working_time/statistics/my`, { credentials: "include" });
            if (!res.ok) throw new Error("Błąd pobierania statystyk.");
            setStatsData(await res.json());
        } catch (err) {
            setStatsError(err.message);
        } finally {
            setLoadingStats(false);
        }
    };

    // >>> DODANE: pobieranie licznika zleceń wymagających przydzielenia <<<
    useEffect(() => {
        if (isAdminOrBoss) fetchAssignableCount();
    }, [isAdminOrBoss]);

    const fetchAssignableCount = async () => {
        setLoadingAssignable(true);
        try {
            const res = await fetch(`${API_URL}/orders`, { credentials: "include" });
            if (!res.ok) throw new Error("Błąd pobierania zleceń");
            const data = await res.json();
            const count = (data || []).filter(o => {
                const execId = o.osoba_wykonujaca_id == null ? null : Number(o.osoba_wykonujaca_id);
                return execId === null || execId === 29;
            }).length;
            setAssignableCount(count);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingAssignable(false);
        }
    };

    /* ================= RENDER ================= */
    return (
        <section className="admin-panel-grid">
            {/* ------------ NAGŁÓWEK ------------ */}
            <h2 className="admin-panel-title">Panel Administratora</h2>

            {/* ------------ KOLUMNA 1 ------------ */}
            <div className="admin-col">
                <h3>Zarządzanie Zleceniami</h3>
                <button className="btn-submit" onClick={() => setShowCloseModal(true)} disabled={loadingClose}>
                    Zlecenia do Zamknięcia ({ordersToClose.length})
                </button>
                <button className="btn-submit" onClick={() => setShowSystemModal(true)}>
                    Zlecenia&nbsp;Systemowe
                </button>
				<button
                    className="btn-submit"
                    onClick={() => setShowAssignOrdersModal(true)}
                    disabled={loadingAssignable}
                >
					Zlecenia Wymagające Przydzielenia ({assignableCount})
				</button>
				<button
				  className="btn-submit"
				  onClick={() => setShowInvoiceModal(true)}
				  disabled={loadingInvoice}
			    >
				  Zlecenia do Fakturowania ({ordersToInvoice.length})
			    </button>
            </div>

            {/* ------------ KOLUMNA 2 ------------ */}
            <div className="admin-col">
                <h3>Organizacja Pracy i Statystyki</h3>
                <button className="btn-submit" onClick={() => navigate("/kalendarz")}>
                    Kalendarz
                </button>
                {statsOptions.map((opt) => (
                    <button key={opt} className="btn-submit" onClick={() => handleOptionClick(opt)}>
                        {opt}
                    </button>
                ))}
            </div>

            {/* ------------ KOLUMNA 3 ------------ */}
            <div className="admin-col">
                <h3>Zarządzanie Systemem i Użytkownikami</h3>
                <button className="btn-submit" onClick={() => setShowAddModal(true)}>
                    Dodaj Użytkownika
                </button>
                <button className="btn-submit" onClick={() => setShowEditModal(true)}>
                    Edytuj Użytkownika
                </button>
                <button className="btn-submit" onClick={() => setShowResetModal(true)}>
                    Zresetuj Hasło Użytkownika
                </button>
                <button className="btn-submit" onClick={() => setShowAddClientModal(true)}>
                    Dodaj Klienta
                </button>
                <button className="btn-submit" onClick={() => setShowAddAreaModal(true)}>
                    Dodaj Obszar
                </button>
                <button className="btn-submit" onClick={() => setShowDeleteModal(true)}>
                    Usuń Obszar / Klienta
                </button>
            </div>

            {message && <p className="admin-message">{message}</p>}

            {/* ---------- MODAL DODAWANIA ---------- */}
            {showAddModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Dodaj Użytkownika</h3>
                        <form onSubmit={handleAddUser}>
                            <input name="login" placeholder="Login" value={formData.login} onChange={handleChange} required />
                            <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} required />
                            <input name="first_name" placeholder="Imię" value={formData.first_name} onChange={handleChange} required />
                            <input name="last_name" placeholder="Nazwisko" value={formData.last_name} onChange={handleChange} required />
                            <label>Data urodzenia:</label>
                            <input type="date" name="birth_date" value={formData.birth_date} onChange={handleChange} required />
                            <label>Data przyjęcia do pracy:</label>
                            <input type="date" name="hire_date" value={formData.hire_date} onChange={handleChange} required />
                            <label>Data odejścia (opc.):</label>
                            <input type="date" name="departure_date" value={formData.departure_date} onChange={handleChange} />
                            <label>Rola:</label>
                            <select name="role" value={formData.role} onChange={handleChange} required>
                                <option>Admin</option> <option>Moderator</option> <option>User</option>
                            </select>
                            <label>Stanowisko:</label>
                            <select name="position" value={formData.position} onChange={handleChange} required>
                                <option>Szef</option> <option>Manager</option> <option>Project Manager</option>
                                <option>Pracownik</option> <option>Logistyk</option> <option>Projektant</option>
                                <option>Programista</option> <option>Gość</option>
                            </select>
                            <div className="modal-buttons">
                                <button type="submit" className="btn-submit">Zapisz</button>
                                <button type="button" className="btn-submit" onClick={() => setShowAddModal(false)}>Anuluj</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {/* --- Pozostałe modale --- */}
            {showEditModal && <EditUsersModal onClose={() => setShowEditModal(false)} onUserUpdated={() => setMessage("✅ Dane zaktualizowane")} />}
            {showResetModal && <ResetPasswordModal onClose={() => setShowResetModal(false)} />}
            {showSystemModal && <SystemOrderGeneratorModal visible onClose={() => setShowSystemModal(false)} onSaved={() => setShowSystemModal(false)} />}
            {showAddClientModal && <AddClientModal onClose={() => setShowAddClientModal(false)} onSave={() => setShowAddClientModal(false)} />}
            {showAddAreaModal && <AddAreaModal onClose={() => setShowAddAreaModal(false)} onSave={() => setShowAddAreaModal(false)} />}
            {showDeleteModal && <DeleteAreaClientModal onClose={() => setShowDeleteModal(false)} onDeleteSuccess={() => { setShowDeleteModal(false); setMessage("✅ Pomyślnie usunięto"); }} />}
			{showAssignOrdersModal && (
			  <AssignOrdersModal
				visible
				onClose={() => {
                    setShowAssignOrdersModal(false);
                    fetchAssignableCount(); // po zamknięciu odśwież licznik
                }}
			  />
			)}
            
            {/* --- Modale z Dashboardu --- */}
            {showStatsModal && <WorkTimeStatisticsModal onClose={() => setShowStatsModal(false)} option={selectedOption} statsData={statsData} loadingStats={loadingStats} statsError={statsError} />}
            {showCloseModal && <CloseOrdersModal orders={ordersToClose} onClose={() => setShowCloseModal(false)} onCloseOrder={handleCloseOrder} onViewOrder={(id) => navigate(`/zlecenia/${id}`)} />}
			{showInvoiceModal && (
			  <InvoiceOrdersModal
				orders={ordersToInvoice}
				onClose={() => setShowInvoiceModal(false)}
				onCloseOrder={handleCloseOrder}
				onViewOrder={(id) => navigate(`/zlecenia/${id}`)}
			  />
			)}
        </section>
    );
}

export default AdminPanel;
